import { Component, OnInit, inject, signal, effect } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ReactiveFormsModule, FormBuilder, FormGroup, Validators, AbstractControl } from '@angular/forms';
import { CotizadorService } from '../../../../core/services/cotizador.service';
import { CotizacionComponent } from '../../components/cotizacion.component';
import { ModalComponent, ModalProps } from '../../modal.component';

@Component({
  selector: 'app-cotizador-page',
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule, CotizacionComponent, ModalComponent],
  templateUrl: './cotizador-page.component.html'
})
export class CotizadorPageComponent implements OnInit {
  private fb = inject(FormBuilder);
  cotizadorService = inject(CotizadorService);

  form!: FormGroup;

  mostrarModal = signal<boolean>(false);
  propsModal = signal<ModalProps | null>(null);

  constructor() {
    effect(() => {
      const resp = this.cotizadorService.successCotiza();
      if (resp && !resp.exito) {
        this.abrirModal(resp.texto || 'Ocurrió un error al procesar la cotización.');
        this.cotizadorService.reinicio();
      }
    });
  }

  ngOnInit(): void {
    this.form = this.fb.group({
      nombre: ['', [Validators.required, Validators.pattern(/^[a-z\u00E0-\u00FC ]+$/i)]],
      apellidos: ['', [Validators.required, Validators.pattern(/^[a-z\u00E0-\u00FC ]+$/i)]],
      correo: ['', [Validators.required, Validators.pattern(/^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@(([[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/)]],
      tTelefono: ['celular', Validators.required],
      telefono: ['', [Validators.required, Validators.pattern(/^\d{10}$/)]],
      genero: ['H', Validators.required],
      fuma: ['N', Validators.required],
      ingreso: ['', [Validators.required, Validators.pattern(/^\d{1,6}$/), this.ingresoMinimoValidator]],
      edad_actual: ['30', [Validators.required, Validators.pattern(/^\d{2}$/)]],
      edadretiro: ['65', [Validators.required, Validators.pattern(/^\d{2}$/)]],
      porSueldo: [75, [Validators.required, Validators.min(50), Validators.max(100)]],
      anios_pension: ['', [Validators.required, this.aniosPensionValidator]],
      ahorro_mensual: ['', [this.ahorroMensualValidator]],
      contacto: [false]
    });
  }

  get formControls() {
    return this.form.controls;
  }

  abrirModal(texto: string, exito = false): void {
    this.propsModal.set({ exito, texto });
    this.mostrarModal.set(true);
  }

  cerrarModal(): void {
    this.mostrarModal.set(false);
  }

  ingresoMinimoValidator(control: AbstractControl) {
    if (!control.value) return null;
    const val = parseInt(control.value, 10);
    return val < 1000 ? { custom: 'El ingreso mensual mínimo es de $1,000 pesos.' } : null;
  }

  aniosPensionValidator(control: AbstractControl) {
    if (!control.value) return null;
    const val = parseInt(control.value, 10);
    if (isNaN(val) || val < 5) return { custom: 'Años mínimos de pensión es de 5 años.' };
    if (val > 25) return { custom: 'Años máximos de pensión son 25 años.' };
    return null;
  }

  ahorroMensualValidator(control: AbstractControl) {
    if (!control.value) return null;
    const regex = /^\d{2,6}$/;
    if (!regex.test(control.value)) {
      return { custom: 'Capture un ahorro mensual válido.' };
    }
    const val = parseInt(control.value, 10);
    if (val < 1000 || val > 150000) {
      return { custom: 'El ahorro mensual mínimo es de $1,000 pesos y máximo de $150,000 pesos.' };
    }
    return null;
  }

  getEdadActualPercent(): number {
    const val = parseInt(this.form.get('edad_actual')?.value || '18', 10);
    return ((val - 18) / (70 - 18)) * 100;
  }

  getEdadRetiroPercent(): number {
    const val = parseInt(this.form.get('edadretiro')?.value || '18', 10);
    return ((val - 18) / (70 - 18)) * 100;
  }

  onEdadActualRangeChange(event: Event): void {
    const value = parseInt((event.target as HTMLInputElement).value, 10);
    let max = parseInt(this.form.get('edadretiro')?.value || '65', 10);
    if (max < 55) max = 55;

    this.form.patchValue({ edad_actual: value, edadretiro: max });
    this.cerrarModal();
  }

  onEdadRetiroRangeChange(event: Event): void {
    let value = parseInt((event.target as HTMLInputElement).value, 10);
    if (value < 55) value = 55;

    this.form.patchValue({ edadretiro: value });
    this.cerrarModal();
  }

  onPorSueldoRangeChange(event: Event): void {
    const value = parseInt((event.target as HTMLInputElement).value, 10);
    this.form.patchValue({ porSueldo: value });
    this.cerrarModal();
  }

  enviar(): void {
    this.cerrarModal();
    this.form.markAllAsTouched();

    if (this.form.invalid) {
      return;
    }

    const values = this.form.value;
    const edadActual = parseInt(values.edad_actual, 10);
    const edadRetiro = parseInt(values.edadretiro, 10);

    if (edadActual <= 55) {
      let textoModal: string | null = null;

      if (edadRetiro >= 55 && edadRetiro <= 59 && !(edadActual >= 18 && edadActual <= 45)) {
        textoModal = 'Favor de seleccionar una edad de retiro superior.';
      } else if (edadRetiro >= 60 && edadRetiro <= 64 && !(edadActual >= 18 && edadActual <= 50)) {
        textoModal = 'Favor de seleccionar una edad de retiro superior.';
      } else if (edadRetiro >= 65 && edadRetiro <= 70 && !(edadActual >= 18 && edadActual <= 55)) {
        textoModal = 'Favor de seleccionar una edad de retiro superior.';
      }

      if (textoModal) {
        this.abrirModal(textoModal);
        return;
      }
    }

    if (!values.contacto) {
      this.abrirModal('Favor de seleccionar el check de consentimiento para contacto y aviso de privacidad.');
      return;
    }

    this.cotizadorService.cotizar(values);
  }

  handleCotizarResultado(values: any): void {
    this.cotizadorService.cotizar(values);
  }

  handleRegresar(): void {
    window.location.reload();
  }
}